import React, { useEffect, useRef, useState } from "react";
import { IconButton, LinearProgress, Tooltip } from "@mui/material";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import { ZoomIn, ZoomOut, RotateLeft, RotateRight } from "@mui/icons-material";
import CoreSpinner from "../CoreSpinner/CoreSpinner";
import { useSelector } from "react-redux";
import { calculateClockOrderList } from "../../../../utils/functions/boundingBox";

function ImageGallery({ url, boundingBox, docStatus }) {
  const appConfig = useSelector((state) => state.app.appConfig);
  const activeBox = appConfig?.modules?.documents?.boundingBox;

  const canvasRef = useRef(null);
  const MAX_SCALE = 4;
  const MIN_SCALE = 0.5;
  const [view, setView] = useState(activeBox ? true : false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.src = url;

    img.onload = () => {
      if (!canvasRef.current) return;
      setLoading(false);
      const angle = rotation % 360;
      let canvasWidth = img.width;
      let canvasHeight = img.height;

      if (angle === 90 || angle === 270) {
        canvasWidth = img.height;
        canvasHeight = img.width;
      }

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Pulisci il canvas prima di ridisegnare
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      // Definisci il colore e lo spessore del bordo
      // ctx.strokeStyle = "red";
      ctx.fillStyle = "rgba(242, 255, 0, 0.18)";
      ctx.lineWidth = 1;

      // Disegna i bounding boxes sull'immagine
      if (boundingBox) {
        boundingBox.forEach((box) => {
          let [x1, y1, x2, y2, x3, y3, x4, y4] = box.boundingBox;
          const sortedList = calculateClockOrderList([
            x1 * img.width - img.width / 2,
            y1 * img.height - img.height / 2,
            x2 * img.width - img.width / 2,
            y2 * img.height - img.height / 2,
            x3 * img.width - img.width / 2,
            y3 * img.height - img.height / 2,
            x4 * img.width - img.width / 2,
            y4 * img.height - img.height / 2,
          ]);
          const padding = 4;
          ctx.beginPath();
          ctx.moveTo(sortedList[0] - padding, sortedList[1] + padding);
          ctx.lineTo(sortedList[2] + padding, sortedList[3] + padding);
          ctx.lineTo(sortedList[4] + padding, sortedList[5] - padding);
          ctx.lineTo(sortedList[6] - padding, sortedList[7] - padding);
          ctx.lineTo(sortedList[0] - padding, sortedList[1] + padding);
          // ctx.stroke();
          ctx.fill(); // Riempi il bounding box
        });
      }

      // Resetta la trasformazione
      ctx.resetTransform();
    };
  }, [url, boundingBox, view, rotation]);

  const zoomIn = () => {
    setZoomLevel(zoomLevel + 0.25);
  };

  const zoomOut = () => {
    setZoomLevel(zoomLevel - 0.25);
  };

  const handleRotateLeft = () => {
    setRotation((rotation - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation((rotation + 90) % 360);
  };

  const handleActiveBox = () => {
    setView(!view);
    setRotation(0);
    setZoomLevel(1);
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div
        style={{ width: "95%" }}
        className="flex items-center justify-between px-4 rounded-t-md bg-stone-50"
      >
        <div className="flex items-center">
          <IconButton
            className="mx-2"
            onClick={zoomOut}
            disabled={zoomLevel === MIN_SCALE}
          >
            <ZoomOut />
          </IconButton>
          <LinearProgress
            className="w-32"
            variant="determinate"
            value={(zoomLevel * 100) / MAX_SCALE}
            color="inherit"
          />
          <IconButton
            className="mx-2"
            onClick={zoomIn}
            disabled={zoomLevel === MAX_SCALE}
          >
            <ZoomIn />
          </IconButton>
        </div>
        <div className="flex items-center">
          <IconButton className="mx-2" onClick={handleRotateLeft}>
            <RotateLeft />
          </IconButton>
          <IconButton className="mx-2" onClick={handleRotateRight}>
            <RotateRight />
          </IconButton>
        </div>
        {activeBox && (
          <div className="flex items-center">
            <Tooltip title="Bounding box">
              <IconButton className="mx-2" onClick={handleActiveBox}>
                <SelectAllIcon
                  className={`${view ? "text-green-600  " : "text-gray"}`}
                />
              </IconButton>
            </Tooltip>
          </div>
        )}
        {docStatus}
      </div>

      <div
        style={{
          overflow: "auto",
          height: "80vh",
          width: "95%",
          border: "1px solid lightgrey",
          borderRadius: 6,
        }}
      >
        <div className="w-full">
          {loading && activeBox ? <CoreSpinner /> : ""}
          {view ? (
            <canvas
              ref={canvasRef}
              style={{
                height: "100%",
                width: "100%",
                transform: `scale(${zoomLevel})`,
                transformOrigin: "0 0",
              }}
            />
          ) : (
            <img
              style={{
                height: "100%",
                width: "100%",
                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
              }}
              src={url}
              alt="text"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageGallery;
